import { useTranslation } from "@sagot/i18n";
import { Avatar, Badge, Icon, Select, type IconName } from "@sagot/ui";
import type { Stage } from "./lead-types";

const CHANNEL_ICONS: Readonly<Record<string, IconName>> = {
  widget: "globe",
  messenger: "message-circle",
  mobile: "smartphone",
  webapp: "app-window",
  email: "mail",
  api: "code",
};

export function channelIcon(channel: string | null | undefined): IconName {
  return (channel ? CHANNEL_ICONS[channel] : undefined) ?? "message-square";
}

export function ChannelBadge({ channel }: { channel: string | null }) {
  const { t } = useTranslation();
  if (!channel) return <span className="text-fg-subtle">—</span>;
  return (
    <Badge>
      <Icon name={channelIcon(channel)} className="size-3" />
      {t(`engage.channels.${channel}`, { defaultValue: channel })}
    </Badge>
  );
}

/** Anonymous visitors ("Visitor #4790") get a person icon instead of initials, as in the mockup. */
export function LeadAvatar({ name, className }: { name: string; className?: string }) {
  if (name.startsWith("Visitor")) {
    return (
      <span className={className ? `avatar ${className}` : "avatar"} aria-hidden="true">
        <Icon name="user" className="size-4" />
      </span>
    );
  }
  return <Avatar name={name} className={className} />;
}

export function IdleFlag({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  return (
    <Badge tone="warning" title={t("engage.leads.idleHint")}>
      <Icon name="clock" className="size-3" />
      {compact ? <span className="sr-only">{t("engage.leads.idle")}</span> : t("engage.leads.idle")}
    </Badge>
  );
}

interface StageSelectProps {
  stages: readonly Stage[];
  value: string;
  onChange: (stageId: string) => void;
  label: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export function StageSelect({ stages, value, onChange, label, disabled, className, id }: StageSelectProps) {
  return (
    <Select
      id={id}
      aria-label={id ? undefined : label}
      className={className}
      value={value}
      disabled={disabled}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    >
      {stages.map((stage) => (
        <option key={stage.id} value={stage.id}>
          {stage.name}
        </option>
      ))}
    </Select>
  );
}
